/**
 * Storefront v6 — what the kitchen sells beyond single dishes: meal plans
 * (with how it works), meal passes, catering packages, and the promo band.
 * Every number is the tenant's (tier prices, credit counts, per-person
 * prices, lead times); nothing is invented.
 */
import React, { useMemo, useState } from 'react';
import { ChevronRight, Users, Clock, Tag, Search } from 'lucide-react';
import { Photo, Price, BlockButton, PaperButton, GhostButton, SectionHead, Chip, Badge, money } from '../primitives';
import { sizesFor } from '../data/media';
import { packComparison } from '../../../storefront/packPricing';
import { cutoffLabel, dateLabel } from '../../../storefront/MealPackBuilder';
import ProductGallery from '../../../storefront/ProductGallery';

const PackDetails = ({ plan, currency }) => {
  if (plan.storefront_selling?.mode !== 'meal_pack') return null;
  const tiers = ['selected_items', 'category_meal'].includes(plan.storefront_selling.pricing_mode) ? [] : plan.pricing?.tiers || [];
  const size = tiers.slice().sort((a, b) => a.quantity - b.quantity)[0]?.quantity;
  const c = packComparison(tiers, size, plan.storefront_selling.recurring_discount_percent || 0);
  const next = plan.next_pack_delivery;
  return <span className="block mt-3 space-y-2 text-xs leading-relaxed">
    {c && <span className="block font-semibold">{money(c.one_time_per_meal, currency)} / meal · {size} meals per delivery</span>}
    {c && plan.storefront_selling.recurring_enabled && <span className="block font-semibold" style={{ color: 'var(--s6-primary-text)' }}>{c.subscription_savings > 0 ? <>Subscribe & save {money(c.subscription_savings, currency)} per {size}-meal pack</> : 'Weekly subscription'} · {money(c.subscription_per_meal, currency)} / meal</span>}
    {next ? <span className="block">Order before {cutoffLabel(next.cutoff_at, next.timezone)} for delivery {dateLabel(next.date)}.</span> : <span className="block">Next menu opening soon.</span>}
  </span>;
};

const planPhoto = (plan) => (Array.isArray(plan?.images) && plan.images[0]) || plan?.image_url || null;
const planTiers = (plan) => (plan?.storefront_selling?.pricing_mode !== 'selected_items' && Array.isArray(plan?.pricing?.tiers) ? plan.pricing.tiers.filter((t) => Number(t?.price) > 0) : []);
export const planFrom = (plan) => {
  if (plan.storefront_selling?.pricing_mode === 'category_meal') return { price: plan.storefront_selling.composed_meal.base_price, quantity: null, many: true, perDish: true };
  if (plan.storefront_selling?.pricing_mode === 'selected_items') {
    const prices = (plan.storefront_selling.item_prices || []).map(p => Number(p.one_time_price)).filter(p => p > 0);
    return prices.length ? { price: Math.min(...prices), quantity: null, many: true, perDish: true } : null;
  }
  const tiers = planTiers(plan);
  if (!tiers.length) return null;
  const cheapest = tiers.reduce((a, b) => (Number(b.price) < Number(a.price) ? b : a));
  return { price: Number(cheapest.price), quantity: Number(cheapest.quantity) || null, many: tiers.length > 1 };
};

/** Past this many plans the section is a catalogue: five rows, then a grid behind "Show all". */
export const PLAN_ROWS_SHOWN = 5;
export const PLAN_SEARCH_FROM = 9;

const PlanTile = ({ plan, currency, onChoose }) => {
  const from = planFrom(plan);
  const photo = planPhoto(plan);
  return (
    <button type="button" onClick={() => onChoose(plan)} className="s6-card s6-card-hover text-left overflow-hidden flex flex-col" data-testid={`s6-plan-tile-${plan.id}`}>
      {photo && <Photo src={photo} alt="" ratio="4 / 3" className="!rounded-none" />}
      <span className="p-4 flex-1 flex flex-col">
        <span className="s6-h3 !text-base leading-snug">{plan.alias || plan.name}</span>
        {from && <span className="text-sm mt-1" style={{ color: 'var(--s6-ink-muted)' }}>{from.many ? 'from ' : ''}<b style={{ color: 'var(--s6-ink)' }}>{money(from.price, currency)}</b>{from.perDish ? ' / dish' : from.quantity ? ` for ${from.quantity} meals` : ''}</span>}
        {plan.pickup_only && <span className="text-xs font-semibold mt-1" style={{ color: 'var(--s6-warn)' }}>Pickup only</span>}
        <PackDetails plan={plan} currency={currency} />
      </span>
    </button>
  );
};

const planIncludes = (plan) => (Array.isArray(plan?.meal_items) ? plan.meal_items : []).map((m) => (m && (m.name || m.item_name)) || '').map((n) => String(n).trim()).filter(Boolean);

export const MealPlans = ({ plans = [], tags = [], currency, copy = {}, theme, onChoose, howItWorks, featuredId = null }) => {
  const [tag, setTag] = useState('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const visible = useMemo(() => {
    let list = plans;
    if (tag !== 'all') {
      const t = tags.find((x) => (x.id || x.name) === tag);
      const ids = new Set(t?.meal_plan_ids || []);
      list = list.filter((p) => ids.has(p.id));
    }
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => `${p.alias || ''} ${p.name || ''} ${p.description || ''}`.toLowerCase().includes(q));
    return list;
  }, [plans, tags, tag, query]);
  if (!plans.length) return null;
  const featured = (featuredId && visible.find((p) => p && p.id === featuredId)) || visible.find(planPhoto) || visible[0];
  const includes = featured ? planIncludes(featured) : [];
  const restAll = visible.filter((p) => p && p.id !== featured?.id);
  const many = restAll.length > PLAN_ROWS_SHOWN;
  const rest = many && !expanded ? restAll.slice(0, PLAN_ROWS_SHOWN) : restAll;
  const searchable = plans.length >= PLAN_SEARCH_FROM;
  return (
    <section id="plans" className="s6-section" data-testid="s6-plans">
      <div className="s6-container">
        <SectionHead eyebrow={copy.eyebrow} title={copy.title} emphasis={copy.emphasis} sub={copy.sub} accentStyle={theme.accentStyle} left />
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="s6-plan-tags">
            <Chip active={tag === 'all'} onClick={() => setTag('all')}>All plans</Chip>
            {tags.map((t) => (
              <Chip key={t.id || t.name} active={tag === (t.id || t.name)} onClick={() => setTag(t.id || t.name)} count={Array.isArray(t.meal_plan_ids) ? t.meal_plan_ids.length : undefined}>
                {t.emoji ? `${t.emoji} ` : ''}{t.name}
              </Chip>
            ))}
          </div>
        )}
        {searchable && (
          <div className="s6-input-pill max-w-md mb-8" data-testid="s6-plan-search">
            <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--s6-ink-muted)' }} aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${plans.length} plans`} className="flex-1 bg-transparent outline-none py-2 text-[15px]" aria-label="Search plans" />
          </div>
        )}
        {!visible.length && (
          <p className="text-sm py-6" style={{ color: 'var(--s6-ink-muted)' }} data-testid="s6-plan-empty">No plans match {query.trim() ? `“${query.trim()}”` : 'that filter'}.</p>
        )}
        {featured && (
          <div className="grid gap-5 lg:grid-cols-12 items-start">
            <article className="lg:col-span-7 s6-card overflow-hidden" data-testid={`s6-plan-${featured.id}`}>
              <div className="grid md:grid-cols-2">
                {featured.images?.length > 1 ? <ProductGallery item={featured} className="md:h-full md:!aspect-auto" /> : <Photo src={planPhoto(featured)} alt={featured.name} ratio="4 / 3" sizes={sizesFor('tile')} className="!rounded-none min-w-0 md:h-full md:min-h-[320px] md:!aspect-auto" />}
                <div className="p-6 md:p-8 flex flex-col">
                  {featured.id === featuredId && <span className="s6-badge mb-3 self-start" data-testid="s6-plan-recommended">Recommended</span>}
                  <h3 className="s6-h2 !text-3xl">{featured.alias || featured.name}</h3>
                  {planTiers(featured).length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {planTiers(featured).map((t) => <span key={t.name || t.quantity} className="s6-badge">{t.name || `${t.quantity} meals`}</span>)}
                    </div>
                  )}
                  {featured.description && <p className="mt-4 text-sm leading-relaxed line-clamp-3" style={{ color: 'var(--s6-ink-muted)' }}>{featured.description}</p>}
                  {includes.length > 0 && (
                    <ul className="mt-4 grid gap-1 text-sm" style={{ color: 'var(--s6-ink-muted)' }} data-testid="s6-plan-includes">
                      {includes.slice(0, 5).map((n) => <li key={n} className="flex gap-2"><span aria-hidden="true" style={{ color: 'var(--s6-primary-text)' }}>✓</span>{n}</li>)}
                      {includes.length > 5 && <li className="text-xs">+ {includes.length - 5} more</li>}
                    </ul>
                  )}
                  {featured.pickup_only && <p className="mt-3 text-xs font-semibold" style={{ color: 'var(--s6-warn)' }}>Pickup only</p>}
                  {planFrom(featured) && (
                    <p className="mt-5 text-sm" style={{ color: 'var(--s6-ink-muted)' }}>
                      {planFrom(featured).many ? 'from ' : ''}<span className="text-3xl font-bold tracking-tight" style={{ color: 'var(--s6-ink)' }}>{money(planFrom(featured).price, currency)}</span>
                      {planFrom(featured).perDish ? ' / dish' : planFrom(featured).quantity ? ` for ${planFrom(featured).quantity} meals` : ''}
                    </p>
                  )}
                  <div className="mt-auto pt-6">
                    <div className="mb-4"><PackDetails plan={featured} currency={currency} /></div>
                    <BlockButton onClick={() => onChoose(featured)} data-testid={`s6-plan-choose-${featured.id}`}>Choose this plan</BlockButton>
                  </div>
                </div>
              </div>
            </article>
            {rest.length > 0 && (
              <div className="lg:col-span-5 s6-card s6-rows overflow-hidden" data-testid="s6-plan-rows">
                {(many && expanded ? rest.slice(0, PLAN_ROWS_SHOWN) : rest).map((p) => {
                  const from = planFrom(p);
                  return (
                    <button key={p.id} type="button" onClick={() => onChoose(p)} className="w-full text-left flex items-center gap-4 p-4 hover:bg-[var(--s6-paper-alt)] transition-colors" data-testid={`s6-plan-${p.id}`}>
                      {planPhoto(p) && <Photo src={planPhoto(p)} alt="" ratio="1 / 1" sizes="80px" width={160} className="w-20 flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="s6-h3 truncate">{p.alias || p.name}</p>
                        {from && <p className="text-sm mt-0.5" style={{ color: 'var(--s6-ink-muted)' }}>{from.many ? 'from ' : ''}<b style={{ color: 'var(--s6-ink)' }}>{money(from.price, currency)}</b>{from.perDish ? ' / dish' : from.quantity ? ` for ${from.quantity} meals` : ''}</p>}
                        {p.pickup_only && <span className="text-xs font-semibold" style={{ color: 'var(--s6-warn)' }}>Pickup only</span>}
                        <PackDetails plan={p} currency={currency} />
                      </div>
                      <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-50" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {many && !expanded && (
          <div className="mt-6 text-center">
            <GhostButton onClick={() => setExpanded(true)} data-testid="s6-plan-show-all">Show all {visible.length} plans</GhostButton>
          </div>
        )}
        {many && expanded && (
          <div className="mt-8" data-testid="s6-plan-grid">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {restAll.slice(PLAN_ROWS_SHOWN).map((p) => <PlanTile key={p.id} plan={p} currency={currency} onChoose={onChoose} />)}
            </div>
            <div className="mt-6 text-center">
              <GhostButton onClick={() => { setExpanded(false); document.getElementById('plans')?.scrollIntoView({ block: 'start' }); }} data-testid="s6-plan-show-fewer">Show fewer</GhostButton>
            </div>
          </div>
        )}
        {howItWorks}
      </div>
    </section>
  );
};

/** Three numbered rhythm lines. `steps` = [{ title, body }] — the merchant's, or the model defaults. */
export const HowItWorks = ({ steps = [], copy = {}, embedded = false }) => {
  const list = steps.filter((s) => s && (s.title || s.body)).slice(0, 4);
  if (!list.length) return null;
  const body = (
    <div data-testid="s6-how">
      {copy.eyebrow && <span className="s6-eyebrow">{copy.eyebrow}</span>}
      <div className={`grid gap-6 ${list.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        {list.map((s, i) => (
          <div key={i} className="flex gap-4 md:border-l md:pl-5" style={{ borderColor: 'var(--s6-line)' }} data-testid={`s6-step-${i}`}>
            <span className="text-4xl font-bold leading-none tabular-nums" style={{ fontFamily: 'var(--s6-heading)', color: 'var(--s6-primary)' }}>{String(i + 1).padStart(2, '0')}</span>
            <div>
              <p className="s6-h3">{s.title}</p>
              {s.body && <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--s6-ink-muted)' }}>{s.body}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  if (embedded) return <div className="mt-12 md:mt-16 pt-10 border-t" style={{ borderColor: 'var(--s6-line)' }}>{body}</div>;
  return <section id="how" className="s6-section s6-surface-alt"><div className="s6-container">{body}</div></section>;
};

/** One field of brand colour for the merchant's own offer line. */
export const PromoBand = ({ text, cta, onCta, photo }) => {
  if (!text) return null;
  return (
    <section id="promo" className="s6-surface-primary overflow-hidden" data-testid="s6-promo">
      <div className="s6-container py-12 md:py-16 grid md:grid-cols-12 items-center gap-8">
        <div className="md:col-span-7">
          <h2 className="s6-h2 !text-[clamp(1.9rem,4vw,3rem)]">{text}</h2>
          {cta && <div className="mt-6"><PaperButton onClick={onCta} data-testid="s6-promo-cta">{cta}</PaperButton></div>}
        </div>
        {photo && <div className="md:col-span-5"><Photo src={photo} alt="" ratio="3 / 2" sizes={sizesFor('tile')} /></div>}
      </div>
    </section>
  );
};

export const MealPasses = ({ programs = [], copy = {}, theme, onBuy }) => {
  if (!programs.length) return null;
  return (
    <section id="passes" className="s6-section s6-surface-dark s6-on-dark rounded-t-[2.25rem] md:rounded-t-[3rem]" data-testid="s6-passes">
      <div className="s6-container">
        <SectionHead eyebrow={copy.eyebrow} title={copy.title} emphasis={copy.emphasis} sub={copy.sub} accentStyle={theme.accentStyle} />
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {programs.map((p) => (
            <article key={p.id} className="rounded-[var(--s6-radius-card)] p-6 flex flex-col" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} data-testid={`s6-pass-${p.id}`}>
              {p.image_url && <Photo src={p.image_url} alt="" ratio="3 / 2" className="mb-4" />}
              <h3 className="s6-h3 !text-xl">{p.public_name || p.name}</h3>
              {p.description && <p className="mt-2 text-sm opacity-80 leading-relaxed line-clamp-3">{p.description}</p>}
              <p className="mt-4 text-sm opacity-80">{p.credit_count} credits</p>
              <p className="mt-1 text-3xl font-bold tracking-tight">{money(p.price, p.currency)}</p>
              {p.savings_label && <p className="mt-1 text-sm font-semibold" style={{ color: 'var(--s6-accent)' }}>{p.savings_label}</p>}
              <div className="mt-auto pt-5">
                <button type="button" onClick={() => onBuy(p)} className="s6-btn w-full" style={{ background: 'var(--s6-on-dark)', color: 'var(--s6-dark)' }} data-testid={`s6-pass-buy-${p.id}`}>Buy this pass</button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Catering = ({ packages = [], availability, dishImages = {}, currency, copy = {}, theme, onOrder }) => {
  if (!packages.length) return null;
  const lead = Number(availability?.min_lead_hours) > 0 ? availability.min_lead_hours : null;
  return (
    <section id="catering" className="s6-section s6-surface-white" data-testid="s6-catering">
      <div className="s6-container">
        <SectionHead eyebrow={copy.eyebrow} title={copy.title} emphasis={copy.emphasis} sub={copy.sub} accentStyle={theme.accentStyle} />
        <div className="space-y-6">
          {packages.map((pkg, i) => {
            const photo = pkg.image_url || (pkg.included_items || []).map((it) => {
              const image = dishImages[it.item_id || it.id];
              return typeof image === 'string' ? image : image?.image_url;
            }).find(Boolean) || null;
            const facts = [
              Number(pkg.min_persons) > 0 && { Icon: Users, text: `From ${pkg.min_persons} guests` },
              pkg.pricing_model === 'per_person' && Number(pkg.price_per_person) > 0 && { Icon: Tag, text: `${money(pkg.price_per_person, currency)} per person` },
              lead && { Icon: Clock, text: `${lead}h notice` },
            ].filter(Boolean);
            const includes = (pkg.included_items || []).map((it) => typeof it === 'string' ? it : it.name || it.item_name).filter(Boolean).slice(0, 8);
            return (
              <article key={pkg.id} className="s6-card overflow-hidden grid md:grid-cols-2" data-testid={`s6-catering-${pkg.id}`}>
                <Photo src={photo} alt={pkg.name} ratio="4 / 3" sizes={sizesFor('tile')} className={`!rounded-none md:h-full ${i % 2 ? 'md:order-2' : ''}`} />
                <div className="p-6 md:p-8 flex flex-col">
                  <h3 className="s6-h2 !text-2xl">{pkg.name}</h3>
                  {facts.length > 0 && <div className="s6-facts mt-3">{facts.map(({ Icon, text }) => <span key={text}><Icon className="w-4 h-4" aria-hidden="true" />{text}</span>)}</div>}
                  {pkg.description && <p className="mt-4 text-sm leading-relaxed" style={{ color: 'var(--s6-ink-muted)' }}>{pkg.description}</p>}
                  {includes.length > 0 && (
                    <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      {includes.map((n) => <li key={n} className="flex gap-2"><span aria-hidden="true" style={{ color: 'var(--s6-primary)' }}>✓</span>{n}</li>)}
                    </ul>
                  )}
                  <div className="mt-auto pt-6"><GhostButton onClick={() => onOrder(pkg)} data-testid={`s6-catering-order-${pkg.id}`}>Order catering <ChevronRight className="w-4 h-4" /></GhostButton></div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
